import {
  MongoReturnId,
  MongoReturnItemId,
  MongoUserId,
} from "@redotech/redo-ids-mongo";
import * as z from "zod";

export const InventoryItemKindSchema = z.enum([
  "PRODUCT",
  "RETURN",
  "UNDECLARED",
  "UNKNOWN",
]);
export type InventoryItemKind = z.infer<typeof InventoryItemKindSchema>;

export const InventoryItemClassSchema = z.enum(["lot", "custom"]);
export type InventoryItemClass = z.infer<typeof InventoryItemClassSchema>;

export const InventoryItemGradeSchema = z.string();
export type InventoryItemGrade = z.infer<typeof InventoryItemGradeSchema>;

export const PASSING_GRADES = new Set<string>(["A", "B", "C"]);

export const InventoryItemDispositionSchema = z.enum([
  "restock",
  "liquidate",
  "donate",
  "dispose",
  "return_to_vendor",
]);
export type InventoryItemDisposition = z.infer<
  typeof InventoryItemDispositionSchema
>;

export const InventoryItemEventTypeSchema = z.enum([
  "restocked",
  "picked",
  "shipped",
  "committed",
  "released",
]);
export type InventoryItemEventType = z.infer<
  typeof InventoryItemEventTypeSchema
>;

// Derived from the item's fulfillment groups; not a stored column.
export const InventoryItemLifecycleStatus = {
  atWarehouse: "at_warehouse",
  packed: "packed",
  shipped: "shipped",
} as const;
export type InventoryItemLifecycleStatus =
  (typeof InventoryItemLifecycleStatus)[keyof typeof InventoryItemLifecycleStatus];
export const InventoryItemLifecycleStatusSchema = z.enum([
  InventoryItemLifecycleStatus.atWarehouse,
  InventoryItemLifecycleStatus.packed,
  InventoryItemLifecycleStatus.shipped,
]);

const InventoryItemRecordSchema_ = z.object({
  id: z.bigint(),
  accountId: z.bigint(),
  kind: InventoryItemKindSchema,
  productId: z.bigint().nullable(),
  returnId: MongoReturnId.fromDbBuffer.nullable(),
  returnItemId: MongoReturnItemId.fromDbBuffer.nullable(),
  locationId: z.bigint(),
  storageLocationId: z.bigint().nullable(),
  quantity: z.bigint(),
  class: InventoryItemClassSchema.nullable(),
  grade: InventoryItemGradeSchema.nullable(),
  disposition: InventoryItemDispositionSchema.nullable(),
  lotNumber: z.string().nullable(),
  expirationDate: z.date().nullable(),
  createdAt: z.date(),
  updatedAt: z.date(),
  deletedAt: z.date().nullable(),
});

export interface InventoryItemRecord {
  id: bigint;
  accountId: bigint;
  kind: InventoryItemKind;
  productId: bigint | null;
  returnId: string | null;
  returnItemId: string | null;
  locationId: bigint;
  storageLocationId: bigint | null;
  quantity: bigint;
  class: InventoryItemClass | null;
  grade: InventoryItemGrade | null;
  disposition: InventoryItemDisposition | null;
  lotNumber: string | null;
  expirationDate: Date | null;
  createdAt: Date;
  updatedAt: Date;
  deletedAt: Date | null;
}

export const InventoryItemRecordSchema: z.ZodType<InventoryItemRecord> =
  InventoryItemRecordSchema_ as z.ZodType<InventoryItemRecord>;

const InventoryItemWithAvailabilitySchema_ = InventoryItemRecordSchema_.extend({
  available: z.bigint(),
  storageLocationName: z.string().nullable(),
  productTitle: z.string().nullable(),
  productSku: z.string().nullable(),
  productHsCode: z.string().nullable(),
  productOriginCountry: z.string().nullable(),
  productWeight: z.coerce.number().nullable(),
  productWeightUnit: z.string().nullable(),
  productPrice: z.coerce.number().nullable(),
  productManufacturerId: z.bigint().nullable(),
  productImageUrl: z.string().nullable(),
});

export interface InventoryItemWithAvailability extends InventoryItemRecord {
  available: bigint;
  storageLocationName: string | null;
  productTitle: string | null;
  productSku: string | null;
  productHsCode: string | null;
  productOriginCountry: string | null;
  productWeight: number | null;
  productWeightUnit: string | null;
  productPrice: number | null;
  productManufacturerId: bigint | null;
  productImageUrl: string | null;
}

export const InventoryItemWithAvailabilitySchema: z.ZodType<InventoryItemWithAvailability> =
  InventoryItemWithAvailabilitySchema_ as z.ZodType<InventoryItemWithAvailability>;

export interface CreateInventoryItemInput {
  accountId: bigint;
  kind: InventoryItemKind;
  locationId: bigint;

  // Required for kind=PRODUCT
  productId?: bigint;

  // Required for kind=RETURN (not required for kind=UNKNOWN)
  returnId?: MongoReturnId;

  // Optional
  returnItemId?: MongoReturnItemId;
  storageLocationId?: bigint;
  quantity?: number;
  class?: InventoryItemClass;
  grade?: InventoryItemGrade;
  disposition?: InventoryItemDisposition;

  // Lot tracking
  lotNumber?: string;
  expirationDate?: Date;

  // For event tracking
  userId?: MongoUserId;
}

export interface DeleteInventoryItemInput {
  id: bigint;
  accountId: bigint;
}

export interface CreateInventoryItemEventInput {
  inventoryItemId: bigint;
  eventType: InventoryItemEventType;
  userId?: MongoUserId;
  payload?: Record<string, unknown>;
}
